import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";

const applications = [
  "Data centres",
  "Communication rooms",
  "UPS rooms",
  "Hospitals",
  "Museums",
  "Oil production plants",
  "Power generation centres",
  "Telecommunication enclosures",
];

const benefits = [
  {
    title: "Rapid Fire Suppression",
    desc: "Unlike most fire suppression systems, the FM-200 extinguishers can attain extinguishing levels in just about 10 seconds. That ensures fires are extinguished quickly before posing any significant threats.",
    icon: "ri-timer-flash-line",
    colorClass: "bg-primary-100",
    iconColor: "text-primary-600",
  },
  {
    title: "Effective on Various Fire Types",
    desc: "FM-200 fire suppression gas systems are ideal for putting out electrical fires, flammable liquids and other common fires in living and work spaces — versatile for a wide range of applications.",
    icon: "ri-fire-line",
    colorClass: "bg-accent-100",
    iconColor: "text-accent-600",
  },
  {
    title: "Eco-Friendly & Safe",
    desc: "FM-200 gas is colourless, odourless and leaves no impacts on the environment. It is even an active ingredient in medical devices like inhalers — totally safe for humans, pets and the environment.",
    icon: "ri-leaf-line",
    colorClass: "bg-secondary-100",
    iconColor: "text-secondary-600",
  },
  {
    title: "Highly Efficient & Economical",
    desc: "Just a small amount of the gas is required to put out fires. Even just one cylinder can serve an entire building, saving operational costs and space. FM-200 systems are cost-effective to install, service and maintain.",
    icon: "ri-money-dollar-circle-line",
    colorClass: "bg-primary-100",
    iconColor: "text-primary-600",
  },
];

export default function FM200FireSuppressionPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "FM-200 Fire Suppression Gas System Singapore",
            description:
              "Learn how FM-200 fire suppression gas systems work, their applications in data centres and commercial spaces, and key benefits for rapid, clean fire suppression in Singapore.",
            publisher: { "@type": "Organization", name: "Ezzogenics" },
          }),
        }}
      />

      <main>
        {/* Hero */}
        <section className="relative pt-20 min-h-[380px] md:min-h-[460px] flex items-end">
          <div className="absolute inset-0">
            <img
              src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/84f66381-caa1-4037-a30e-24ebded0c9cd_compressed_Depositphotos_218634112_l-2015.webp"
              alt="FM-200 fire suppression gas cylinder with brass valves and red piping installed in commercial building"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/50 to-foreground-950/20"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 py-10 md:py-14 max-w-7xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-xs text-background-300 flex-wrap">
                <li><Link to="/" className="hover:text-background-50 transition-colors">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li><Link to="/resources" className="hover:text-background-50 transition-colors">Resources</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-background-50 font-medium">FM-200 Fire Suppression</li>
              </ol>
            </nav>
            <span className="inline-block text-xs font-semibold bg-primary-500 text-background-50 rounded-full px-3 py-1 mb-4">
              Gas Suppression Systems
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 leading-tight mb-4 max-w-3xl">
              FM-200 Fire Suppression <span className="text-primary-400">Gas System</span>
            </h1>
            <p className="text-background-200 text-base md:text-lg max-w-2xl leading-relaxed">
              FM-200 fire suppression gas systems are renowned fire protection systems that facilitate the rapid, safe and clean extinguishing of fires in living and work areas — proven effective in both occupied and protected spaces.
            </p>
          </div>
        </section>

        {/* Intro & Applications */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
                Where It's Used
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                Recommended Applications
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed mb-6">
                FM-200 systems are mainly recommended for suppressing fires in sensitive environments where water or other agents could cause more damage. Quality FM-200 fire suppression systems can play a major role in saving lives and minimizing the destruction to assets during fire emergencies.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {applications.map((app) => (
                  <div key={app} className="flex items-center gap-3 bg-background-100 rounded-lg px-4 py-3 border border-background-200/70">
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-check-line text-primary-600 text-xs"></i>
                    </div>
                    <span className="text-foreground-700 text-sm">{app}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden h-72 md:h-96">
                  <img
                    src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/1975e5ce-06f0-465f-a9c7-c75fdb17925a_compressed_0567ba9a-3db5-423b-b422-896f366b732f.webp"
                    alt="Wall-mounted emergency light box with wiring installed in Singapore building"
                    title="Ezzogenics emergency lighting installation"
                    className="w-full h-full object-cover"
                  />
                </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="bg-background-100 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
                The Science
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                How FM-200 Fire Suppression <span className="text-primary-500">Gas Systems Work</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              <div className="space-y-5">
                <div className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <span className="text-primary-600 font-bold text-sm">1</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground-900 mb-2">Eliminating Heat Elements</h3>
                      <p className="text-foreground-600 text-sm leading-relaxed">
                        FM-200 gas is a waterless fire suppression system that uses FM-200 gas to extinguish fires. The gas mainly works by eliminating the heat elements of free radicals from the flames, thereby suppressing the burning process.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <span className="text-primary-600 font-bold text-sm">2</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground-900 mb-2">Preventing Re-Ignition</h3>
                      <p className="text-foreground-600 text-sm leading-relaxed">
                        FM-200 gas has unique characteristics that not only remove the heat elements but also prevents the flames from re-igniting after they are extinguished. It does that by depriving the flames of oxygen, forcing them to cool off and eventually stop the fire.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-background-50 rounded-2xl p-6 border border-background-200/70">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <span className="text-primary-600 font-bold text-sm">3</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground-900 mb-2">Delivery Methods</h3>
                      <p className="text-foreground-600 text-sm leading-relaxed">
                        FM-200 gas can be discharged through various ways to affected areas — including through natural ventilation via pipes linked to the parts of the building where there is fire, or via portable pressurized cylinders that can be easily used to stop fires without any complex piping networks.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-accent-50 border border-accent-200/70 rounded-2xl p-8 flex flex-col justify-between">
                <div>
                  <i className="ri-test-tube-line text-4xl text-accent-500 mb-4 block"></i>
                  <h3 className="font-heading text-xl font-bold text-foreground-950 mb-3">Key Properties of FM-200 Gas</h3>
                  <ul className="space-y-3 mb-6">
                    {[
                      "Colourless and odourless — does not affect occupants",
                      "Leaves no residue on surfaces and equipment",
                      "Electrically non-conductive — safe around electronics",
                      "Active ingredient in medical inhalers — non-toxic",
                      "No ozone depletion potential",
                      "Fast discharge — achieves extinguishing level in ~10 seconds",
                    ].map((prop) => (
                      <li key={prop} className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-200 flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-accent-700 text-xs"></i>
                        </div>
                        <span className="text-foreground-700 text-sm leading-relaxed">{prop}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap"
                >
                  Enquire About FM-200 Systems
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-3">
              Advantages
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Benefits of FM-200 <span className="text-primary-500">Gas Systems</span>
            </h2>
            <p className="text-foreground-600 text-base max-w-xl mx-auto">
              To determine whether FM-200 gas systems are the best fire protection systems for your building, check out some of its unique benefits.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-background-100 rounded-2xl p-6 border border-background-200/70 flex items-start gap-4">
                <div className={`w-12 h-12 flex items-center justify-center rounded-full flex-shrink-0 ${b.colorClass}`}>
                  <i className={`${b.icon} text-xl ${b.iconColor}`}></i>
                </div>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">{b.title}</h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-background-100 border-t border-background-200/70 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              Get the Best FM-200 Fire Suppression Systems in Singapore
            </h2>
            <p className="text-foreground-600 text-base max-w-xl mx-auto mb-8">
              Ezzogenics offers the best selection of fire suppression systems for residential, commercial and industrial spaces. Contact us today for reliable and efficient fire suppression systems.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Contact Us Today
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-primary-300 text-primary-600 text-sm font-semibold hover:bg-primary-50 transition-colors whitespace-nowrap"
              >
                View Our Services
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}